import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Save, AlertCircle, CheckCircle2, Plus, Trash2, RefreshCw, Info, XCircle } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';


import { Button } from '../../components/ui/Button';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useQuery } from '@tanstack/react-query';
import { useBillingSettings, useUpdateBillingSettings } from '../../hooks/useBillingSettings';
import { useStripeConnectAccount, useCreateConnectAccount, useGetOnboardingLink, useRefreshConnectAccountStatus } from '../../hooks/useStripeConnect';
import { useSaaSSubscription } from '../../hooks/useSaaSSubscription';
import { useCompanyFeeSchedule, type FeeScheduleLineItem } from '../../hooks/useFeeSchedules';
import { FEE_RECIPIENT_LABELS } from '../../utils/feeScheduleLabels';
import { supabase } from '../../lib/supabase';

function formatFee(item: FeeScheduleLineItem): string {
  const parts: string[] = [];
  if (item.fee_percentage !== null) {
    parts.push(`${(item.fee_percentage * 100).toFixed(1)}%`);
  }
  if (item.flat_amount_cents !== null) {
    parts.push(`$${(item.flat_amount_cents / 100).toFixed(2)}`);
  }
  return parts.join(' + ') || 'Included';
}

interface LateFeeStage {
  days: number;
  amount: number;
}

interface MaxTotalLateFeeConfig {
  max_amount: number;
  days_until_cancellation: number;
}

type LateFeeConfig = LateFeeStage[] | MaxTotalLateFeeConfig;

// Type guard to check if config is MaxTotalLateFeeConfig
function isMaxTotalConfig(config: LateFeeConfig): config is MaxTotalLateFeeConfig {
  return !Array.isArray(config) && typeof config === 'object' && 'max_amount' in config;
}

export default function BillingSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: currentCompanyId, role, isLoading: isLoadingCompany } = useCurrentCompany();
  const { data: billingEnabled, isLoading: isLoadingBillingToggle } = useQuery({
    queryKey: ['billing-enabled', currentCompanyId],
    enabled: !!currentCompanyId,
    queryFn: async () => {
      if (!currentCompanyId) return false;
      const { data, error } = await supabase
        .from('companies')
        .select('enable_billing')
        .eq('id', currentCompanyId)
        .maybeSingle<{ enable_billing: boolean | null }>();

      if (error || !data) return false;
      return data.enable_billing ?? false;
    },
    staleTime: 5 * 60 * 1000,
  });
  
  // Role check - only owners and admins can access subscription settings
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  const isOwner = role === 'owner';
  const { data: settings, isLoading, error } = useBillingSettings(currentCompanyId);
  const updateMutation = useUpdateBillingSettings();
  const { data: connectAccount, isLoading: isLoadingConnect, refetch: refetchConnectAccount } = useStripeConnectAccount(currentCompanyId);
  const createConnectAccount = useCreateConnectAccount();
  const getOnboardingLink = useGetOnboardingLink();
  const refreshAccountStatus = useRefreshConnectAccountStatus();
  const { data: saasSubscription, isLoading: isLoadingSaaS } = useSaaSSubscription();
  const { data: feeSchedule, isLoading: isLoadingFeeSchedule } = useCompanyFeeSchedule(currentCompanyId ?? null);
  
  // Check if company is on trial
  const isOnTrial = Boolean(saasSubscription?.status === 'trialing' && saasSubscription?.trial_end_date);
  const trialDaysRemaining = isOnTrial && saasSubscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(saasSubscription.trial_end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [refreshStatusData, setRefreshStatusData] = useState<{
    hasOutstandingRequirements?: boolean;
    currently_due?: string[];
    past_due?: string[];
    pending_verification?: string[];
    incompleteReasons?: string[];
  } | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const originalSettingsRef = useRef<string | null>(null);
  const [formData, setFormData] = useState({
    payment_provider: 'rollops' as 'rollops' | 'self-managed',
    billing_strategy: 'monthly_fixed_date' as 'monthly_fixed_date' | 'on_signup',
    fixed_billing_day: 1 as number | null,
    late_fee_strategy: 'staged' as 'staged' | 'max_total',
    late_fee_config: [] as LateFeeConfig,
    cancellation_notice_days: 30,
    additional_terms_and_conditions: '',
    auto_cancellation_enabled: false,
    auto_cancellation_days_overdue: null as number | null,
  });

  // Initialize form data when settings load
  useEffect(() => {
    if (settings) {
      const lateFeeStrategy = settings.late_fee_strategy || 'staged';
      // If staged fees and config is empty array, initialize with default row
      let lateFeeConfig = settings.late_fee_config;
      if (lateFeeStrategy === 'staged') {
        if (Array.isArray(lateFeeConfig) && lateFeeConfig.length === 0) {
          lateFeeConfig = [{ days: 0, amount: 0 }];
        } else if (!lateFeeConfig) {
          lateFeeConfig = [{ days: 0, amount: 0 }];
        }
      } else {
        lateFeeConfig = lateFeeConfig || { max_amount: 0, days_until_cancellation: 30 };
      }
      
      const initialFormData = {
        payment_provider: settings.payment_provider || 'rollops',
        billing_strategy: settings.billing_strategy || 'monthly_fixed_date',
        fixed_billing_day: settings.fixed_billing_day ?? 1,
        late_fee_strategy: lateFeeStrategy,
        late_fee_config: lateFeeConfig,
        cancellation_notice_days: settings.cancellation_notice_days || 30,
        additional_terms_and_conditions: settings.additional_terms_and_conditions || '',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        auto_cancellation_enabled: (settings as any).auto_cancellation_enabled || false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        auto_cancellation_days_overdue: (settings as any).auto_cancellation_days_overdue || null,
      };
      
      setFormData(initialFormData);
      // Store original settings for comparison
      originalSettingsRef.current = JSON.stringify(initialFormData);
    }
  }, [settings]);

  // Handle return from Stripe onboarding
  useEffect(() => {
    const returnParam = searchParams.get('return');
    const refreshParam = searchParams.get('refresh');
    
    if ((returnParam === 'true' || refreshParam === 'true') && currentCompanyId) {
      // Remove query parameters from URL
      setSearchParams({}, { replace: true });
      
      // Refresh account status from Stripe
      refreshAccountStatus.refresh(currentCompanyId)
        .then((result) => {
          // Store refresh response data for UI messaging
          setRefreshStatusData({
            hasOutstandingRequirements: result.hasOutstandingRequirements,
            currently_due: result.currently_due,
            past_due: result.past_due,
            pending_verification: result.pending_verification,
            incompleteReasons: result.incompleteReasons,
          });
          
          // Refetch account data from database
          refetchConnectAccount();
          
          if (result.isOnboardingComplete) {
            showNotification('success', 'Stripe account onboarding completed successfully!');
            setRefreshStatusData(null); // Clear status data when complete
          } else if (result.status === 'restricted') {
            showNotification('error', 'Account setup incomplete. Please complete all required information.');
          } else {
            showNotification('success', 'Account status updated. Please complete onboarding if needed.');
          }
        })
        .catch((err) => {
          console.error('Error refreshing account status:', err);
          // Still refetch from database in case webhook updated it
          refetchConnectAccount();
          showNotification('error', 'Failed to refresh account status. Please refresh the page.');
        });
    }
  }, [searchParams, currentCompanyId, refreshAccountStatus, refetchConnectAccount, setSearchParams]);

  // Clear refresh status data when account becomes active
  useEffect(() => {
    if (connectAccount?.stripe_connect_account_status === 'active' && connectAccount?.stripe_connect_onboarding_complete) {
      setRefreshStatusData(null);
    }
  }, [connectAccount?.stripe_connect_account_status, connectAccount?.stripe_connect_onboarding_complete]);

  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const validateBillingSettings = (): string[] => {
    const issues: string[] = [];
    
    // Validate late fees configuration
    if (formData.late_fee_strategy === 'staged') {
      const stages = formData.late_fee_config as LateFeeStage[];
      if (!stages || stages.length === 0) {
        issues.push('Add at least one late fee stage');
      } else {
        // Check if all stages are just default zeros (not configured)
        const hasConfiguredStage = stages.some(stage => stage.days > 0 || stage.amount > 0);
        if (!hasConfiguredStage) {
          issues.push('Configure at least one late fee stage with days and amount');
        }
      }
    } else if (formData.late_fee_strategy === 'max_total') {
      const config = formData.late_fee_config as { max_amount: number; days_until_cancellation: number };
      if (!config || config.max_amount === undefined || config.days_until_cancellation === undefined) {
        issues.push('Configure the maximum late fee amount and days until cancellation');
      } else if (config.max_amount < 0 || config.days_until_cancellation < 0) {
        issues.push('Enter valid values for maximum late fee amount and days until cancellation');
      }
    }

    // Validate billing day if using fixed date strategy
    if (formData.billing_strategy === 'monthly_fixed_date') {
      if (!formData.fixed_billing_day || formData.fixed_billing_day < 1 || formData.fixed_billing_day > 31) {
        issues.push('Select a valid billing day (1-31) for the fixed monthly date strategy');
      }
    }

    // Validate cancellation notice days
    if (formData.cancellation_notice_days < 0) {
      issues.push('Enter a valid cancellation notice period (0 or more days)');
    }

    // Validate auto-cancellation if enabled
    if (formData.auto_cancellation_enabled && (!formData.auto_cancellation_days_overdue || formData.auto_cancellation_days_overdue <= 0)) {
      issues.push('Enter days overdue for auto-cancellation (must be greater than 0)');
    }

    return issues;
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!originalSettingsRef.current) return false;
    const currentFormData = JSON.stringify(formData);
    return currentFormData !== originalSettingsRef.current;
  }, [formData]);

  // Handle navigation attempt
  const handleNavigationAttempt = useCallback((targetPath: string) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(targetPath);
      setShowUnsavedModal(true);
      return false;
    }
    return true;
  }, [hasUnsavedChanges]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        window.history.pushState(null, '', location.pathname);
        setPendingNavigation(null);
        setShowUnsavedModal(true);
      }
    };

    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedChanges, location.pathname]);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Intercept navigation
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href && !link.href.startsWith('mailto:') && !link.href.startsWith('tel:')) {
        try {
          const url = new URL(link.href);
          if (url.origin === window.location.origin && url.pathname !== location.pathname) {
            if (hasUnsavedChanges()) {
              e.preventDefault();
              handleNavigationAttempt(url.pathname);
            }
          }
        } catch (_err) {
          // Invalid URL, ignore
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [hasUnsavedChanges, location.pathname, handleNavigationAttempt]);

  // Handle modal actions
  const handleConfirmLeave = () => {
    setShowUnsavedModal(false);
    originalSettingsRef.current = null; // Clear original to allow navigation
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else {
      window.history.back();
    }
  };

  const handleCancelLeave = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  const handleSave = async () => {
    if (!currentCompanyId) return;

    // Validate before saving
    const validationIssues = validateBillingSettings();
    if (validationIssues.length > 0) {
      showNotification('error', validationIssues[0]);
      return;
    }

    try {
      // Sync auto_cancellation_days_overdue with days_until_cancellation if using max_total strategy
      let autoCancellationDaysOverdue = formData.auto_cancellation_days_overdue;
      if (formData.auto_cancellation_enabled && formData.late_fee_strategy === 'max_total' && isMaxTotalConfig(formData.late_fee_config)) {
        autoCancellationDaysOverdue = formData.late_fee_config.days_until_cancellation || 30;
      }

      await updateMutation.update({
        companyId: currentCompanyId,
        billing_strategy: formData.billing_strategy,
        fixed_billing_day: formData.fixed_billing_day,
        late_fee_strategy: formData.late_fee_strategy,
        late_fee_config: formData.late_fee_config,
        cancellation_notice_days: formData.cancellation_notice_days,
        additional_terms_and_conditions: formData.additional_terms_and_conditions,
        auto_cancellation_enabled: formData.auto_cancellation_enabled,
        auto_cancellation_days_overdue: autoCancellationDaysOverdue,
      });

      // Check if settings are complete (has payment_provider)
      const settingsComplete = formData.payment_provider !== null && formData.payment_provider !== undefined;

      // Update original settings ref after successful save
      originalSettingsRef.current = JSON.stringify(formData);

      // Check if automated billing signup is enabled
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: automatedEnabledData } = await (supabase.rpc as any)('is_automated_billing_signup_enabled');

      const automatedEnabled = automatedEnabledData === true;
      const hasActivePaidSubscription = saasSubscription?.status === 'active';

      // Handle trial users differently - don't notify admins, show enrollment message
      if (isOnTrial && billingEnabled === false && !settingsComplete) {
        showNotification(
          'info',
          'To complete billing setup, please enroll in a RollOps subscription first. Once you have an active subscription, you can finish and submit your billing profile for enablement.',
        );
        // Only navigate if user has required role (owner/admin) to avoid route protection issues
        if (isOwnerOrAdmin) {
          setTimeout(() => {
            navigate('/subscriptions-plans/rollops');
          }, 2000);
        }
      } else if (isOnTrial && billingEnabled === false && settingsComplete) {
        // Trial user with complete settings - still need to enroll first
        showNotification(
          'info',
          'Your billing settings are saved. To enable billing and start processing payments, please enroll in a RollOps subscription first.',
        );
        // Only navigate if user has required role (owner/admin) to avoid route protection issues
        if (isOwnerOrAdmin) {
          setTimeout(() => {
            navigate('/subscriptions-plans/rollops');
          }, 2000);
        }
      } else if (!isOnTrial && billingEnabled === false && currentCompanyId) {
        // Check if we should auto-enable billing
        if (automatedEnabled && hasActivePaidSubscription && settingsComplete) {
          // Automatically enable billing
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error: enableError } = await (supabase.from as any)('companies')
            .update({ enable_billing: true })
            .eq('id', currentCompanyId);

          if (enableError) {
            console.warn('[BillingSettings] Error auto-enabling billing:', enableError);
            // Fall through to manual approval flow
          } else {
            showNotification('success', 'Billing enabled automatically. You can now process payments.');
            return;
          }
        }

        // Only show approval message if:
        // 1. Automated billing is NOT enabled AND
        // 2. Settings are NOT complete (no payment_provider yet)
        // If settings are complete, no need for approval message - settings are already configured
        if (!automatedEnabled && !settingsComplete) {
          // Non-trial user with billing disabled and incomplete settings - notify admins for approval
          try {
            const { error: notifyError } = await supabase.functions.invoke(
              'notify-billing-profile-ready',
              {
                body: { company_id: currentCompanyId },
              },
            );

            if (notifyError) {
              console.warn('[BillingSettings] Error notifying admins:', notifyError);
              // Continue anyway - don't block save
            }
          } catch (error) {
            console.warn('[BillingSettings] Error calling notify function:', error);
            // Continue anyway - don't block save
          }

          showNotification(
            'success',
            'Subscription settings saved and submitted for approval. A RollOps team member will review your configuration and enable billing when ready.',
          );
        } else if (!automatedEnabled && settingsComplete) {
          // Settings are complete but automated billing is disabled - just save, no approval message needed
          showNotification('success', 'Subscription settings saved successfully.');
        } else {
          // Automated billing enabled - settings will auto-enable when conditions are met
          showNotification('success', 'Subscription settings saved successfully. Billing will be enabled automatically when you have an active paid subscription.');
        }
      } else {
        showNotification('success', 'Subscription settings saved successfully');
      }
    } catch (err) {
      // Check if it's a validation error from the server
      const error = err instanceof Error ? err : new Error(String(err));
      const errorMessage = error.message.toLowerCase();
      
      if (errorMessage.includes('validation') || errorMessage.includes('invalid') || errorMessage.includes('required')) {
        showNotification('error', error.message || 'Please check your input and try again.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('timeout')) {
        showNotification('error', 'Network error. Please check your connection and try again.');
      } else {
        // Generic error - log it but show user-friendly message
        console.error('Error saving billing settings:', err);
        showNotification('error', 'Failed to save billing settings. Please try again or contact support if the issue persists.');
      }
    }
  };

  const addLateFeeStage = () => {
    if (formData.late_fee_strategy === 'staged') {
      const stages = formData.late_fee_config as LateFeeStage[];
      setFormData({
        ...formData,
        late_fee_config: [...stages, { days: 0, amount: 0 }],
      });
    }
  };

  const removeLateFeeStage = (index: number) => {
    if (formData.late_fee_strategy === 'staged') {
      const stages = formData.late_fee_config as LateFeeStage[];
      setFormData({
        ...formData,
        late_fee_config: stages.filter((_, i) => i !== index),
      });
    }
  };

  // State to track raw input values for currency fields (allows natural typing)
  const [currencyInputs, setCurrencyInputs] = useState<Record<string, string>>({});
  const [maxAmountInput, setMaxAmountInput] = useState<string>('');

  // Parse currency input to number (handles "15", "15.5", "$15.50", etc.)
  const parseCurrencyInput = (value: string): number => {
    if (!value || value.trim() === '') return 0;
    // Remove all non-numeric characters except decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    if (!cleaned) return 0;
    // Handle decimal point
    const parts = cleaned.split('.');
    if (parts.length > 1) {
      // Limit to 2 decimal places
      const decimalPart = parts.slice(1).join('').substring(0, 2);
      return parseFloat(`${parts[0]}.${decimalPart}`) || 0;
    }
    return parseFloat(parts[0]) || 0;
  };

  // Format currency display (e.g., 5.00 -> "$5.00")
  const formatCurrencyDisplay = (value: number): string => {
    if (value === 0 || isNaN(value)) return '';
    return `$${value.toFixed(2)}`;
  };

  // Handle currency input change (store raw value, don't format immediately)
  const handleCurrencyChange = (index: number, value: string) => {
    // Limit to reasonable length
    if (value.length > 15) return;
    
    // Store raw input value
    const inputKey = `stage_${index}`;
    setCurrencyInputs(prev => ({ ...prev, [inputKey]: value }));
    
    // Parse and update the actual amount (for validation)
    const numericValue = parseCurrencyInput(value);
    updateLateFeeStage(index, 'amount', numericValue);
  };

  // Handle currency input blur (format on blur)
  const handleCurrencyBlur = (index: number) => {
    const inputKey = `stage_${index}`;
    const rawValue = currencyInputs[inputKey] || '';
    const numericValue = parseCurrencyInput(rawValue);
    
    // Clear raw input and let formatted display take over
    setCurrencyInputs(prev => {
      const updated = { ...prev };
      delete updated[inputKey];
      return updated;
    });
    
    // Update with final parsed value
    updateLateFeeStage(index, 'amount', numericValue);
  };

  // Get display value for currency input (raw if typing, formatted if not)
  const getCurrencyDisplayValue = (index: number, amount: number): string => {
    const inputKey = `stage_${index}`;
    if (currencyInputs[inputKey] !== undefined) {
      return currencyInputs[inputKey];
    }
    return amount > 0 ? formatCurrencyDisplay(amount) : '';
  };

  // Handle days input change with validation
  const handleDaysChange = (index: number, value: string) => {
    // Limit to 4 characters (max 9999 days)
    if (value.length > 4) return;
    
    // Only allow numbers
    const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    updateLateFeeStage(index, 'days', numericValue);
  };

  const updateLateFeeStage = (index: number, field: 'days' | 'amount', value: number) => {
    if (formData.late_fee_strategy === 'staged') {
      const stages = [...(formData.late_fee_config as LateFeeStage[])];
      stages[index] = { ...stages[index], [field]: value };
      setFormData({
        ...formData,
        late_fee_config: stages,
      });
    }
  };

  if (isLoadingCompany || isLoading) {
    return (
        <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
            <p className="mt-2 text-[#A9A3A3]">Loading...</p>
          </div>
        </div>
    );
  }

  // Role check - only owners and admins can access billing settings
  // Route guard handles redirect, but this provides defense in depth
  if (!isOwnerOrAdmin) {
    return (
        <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p className="text-white/80 mb-6">Only owners and admins can access subscription settings.</p>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </div>
        </div>
    );
  }

  // Note: We no longer block access when billing is disabled
  // Users can configure settings for approval workflow
  // Approval notice will be shown in the form

  if (error) {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-[#111010]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 text-lg mb-2">Failed to load billing settings</p>
            <p className="text-[#A9A3A3] text-sm mb-4">{error instanceof Error ? error.message : 'Unknown error'}</p>
            <Button onClick={() => window.location.reload()} className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]" data-testid="billing-settings-error-retry-button">
              Retry
            </Button>
          </div>
        </div>
    );
  }

  return (
    <>
      <div className="w-full min-h-full flex flex-col bg-[#111010]">
        {/* Notification Toast */}
        {notification && (
          <div
            className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-top-5 max-w-sm ${
              notification.type === 'success'
                ? 'bg-green-600 text-[#EBFFEE]'
                : notification.type === 'info'
                  ? 'bg-blue-600 text-[#EBFFEE]'
                  : 'bg-red-600 text-[#EBFFEE]'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            ) : notification.type === 'info' ? (
              <Info className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="font-medium text-sm sm:text-base flex-1">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-70 transition-opacity text-lg leading-none"
              data-testid="billing-settings-notification-close-button"
            >
              ×
            </button>
          </div>
        )}

        <div className="pb-4 pt-4">
            {/* Trial Notice - Show when on trial */}
            {isOwner && isOnTrial && !isLoadingSaaS && (
              <Card className="bg-blue-600/20 border-blue-600/50 mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-blue-400 mb-2">
                        Free Trial Active
                      </h3>
                      <p className="text-white/90 text-sm mb-2">
                        Credit card processing and RollOps Managed billing are not available during your trial. 
                        You can create subscriptions, manage cash payments, and receive reports (daily cancellations/signups, monthly reports).
                      </p>
                      <p className="text-white/90 text-sm mb-3">
                        You have <strong className="text-blue-400">{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</strong> remaining on your free trial.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {isOwnerOrAdmin ? (
                          <Button
                            onClick={() => navigate('/subscriptions-plans/rollops')}
                            className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] text-sm"
                          >
                            Sign Up for Pro Version Today
                          </Button>
                        ) : (
                          <p className="text-blue-400 text-sm">
                            Contact your gym owner or admin to upgrade to Pro.
                          </p>
                        )}
                        <a
                          href="mailto:support@rollops.pro?subject=Trial Questions"
                          className="inline-flex items-center px-4 py-2 bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] rounded-md text-sm font-semibold transition-colors border border-[#504A4A]"
                        >
                          Contact Support
                        </a>
                      </div>
                      <p className="text-white/80 text-xs mt-3">
                        If you have questions, feel free to chat with support or give us a call and we will be happy to assist.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Approval Notice - Show when billing is disabled AND settings exist in DB (meaning configured but not approved) */}
            {!isLoadingBillingToggle && !isLoading && billingEnabled === false && settings && settings.id && !isOnTrial && (
              <Card className="bg-yellow-600/20 border-yellow-600/50 mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                        Subscription Settings Pending Approval
                      </h3>
                      <p className="text-white/90 text-sm mb-2">
                        You can configure your subscription settings now. Once saved, your configuration
                        will be submitted for RollOps team review. Credit card processing will remain disabled
                        until approved, but you can explore and configure all settings in the
                        meantime.
                      </p>
                      <p className="text-white/80 text-sm">
                        After approval, credit card processing will be enabled automatically with your configured
                        settings.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

        {/* Gym Subscriptions Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-[#EBFFEE] mb-4">Gym Subscriptions</h2>
          <p className="text-sm text-[#A9A3A3] mb-6">
            Configure your customer-facing membership offerings, including plans, pricing, billing cycles, trials, discounts, taxes, and fees.
          </p>

        {/* Payment Provider Selection */}
        <Card className={`bg-[#221F1F] border-[#504A4A] mb-6 ${isOnTrial ? 'opacity-60' : ''}`}>
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Payment Provider</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isOnTrial && (
              <div className="mb-4 p-3 bg-blue-600/20 border border-blue-600/50 rounded-md">
                <p className="text-blue-400 text-sm">
                  Payment provider selection is not available during your free trial. Upgrade to Pro to enable credit card processing.
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">How would you like to handle payments?</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="payment_provider"
                    value="rollops"
                    checked={formData.payment_provider === 'rollops'}
                    onChange={() => setFormData({ ...formData, payment_provider: 'rollops' })}
                    disabled={!!isOnTrial}
                    className="w-4 h-4 text-rollops-accent"
                    data-testid="billing-settings-payment-provider-rollops-radio"
                  />
                  <span className={`${isOnTrial ? 'text-[#A9A3A3]' : 'text-[#EBFFEE]'}`}>Payments Handled by RollOps</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="payment_provider"
                    value="self-managed"
                    checked={formData.payment_provider === 'self-managed'}
                    onChange={() => setFormData({ ...formData, payment_provider: 'self-managed' })}
                    disabled={!!isOnTrial}
                    className="w-4 h-4 text-rollops-accent"
                    data-testid="billing-settings-payment-provider-manual-radio"
                  />
                  <span className={`${isOnTrial ? 'text-[#A9A3A3]' : 'text-[#EBFFEE]'}`}>Payments Handled by You</span>
                </label>
              </div>
              {formData.payment_provider === 'self-managed' && (
                <div className="mt-3 p-3 bg-red-600/30 border border-red-600 rounded-md">
                  <p className="text-white text-sm">
                    Note: RollOps cannot block class enrollments if billing is not handled by RollOps currently.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stripe Connect Onboarding - Only show if RollOps provider */}
        {formData.payment_provider === 'rollops' && !isOnTrial && (
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Payment Account Setup</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingConnect ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
              </div>
            ) : connectAccount?.stripe_connect_account_status === 'active' && connectAccount?.stripe_connect_onboarding_complete ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Payment account connected and active</span>
                </div>
                <p className="text-[#A9A3A3] text-sm">
                  Your Stripe Connect account is set up and ready to accept payments. 
                  Payments will go directly to your account, and RollOps will automatically collect platform fees.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      if (!currentCompanyId) return;
                      try {
                        const result = await getOnboardingLink.getLink(currentCompanyId, true);
                        if (result.url) {
                          window.location.href = result.url;
                        }
                      } catch (_err) {
                        showNotification('error', 'Failed to get account update link');
                      }
                    }}
                    disabled={getOnboardingLink.isLoading}
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
                    data-testid="billing-settings-update-account-settings-button"
                  >
                    Update Account Settings
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!currentCompanyId) return;
                      try {
                        await refreshAccountStatus.refresh(currentCompanyId);
                        refetchConnectAccount();
                        showNotification('success', 'Account status refreshed');
                      } catch (_err) {
                        showNotification('error', 'Failed to refresh account status');
                      }
                    }}
                    disabled={refreshAccountStatus.isLoading}
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A] flex items-center gap-2"
                    data-testid="billing-settings-refresh-status-button"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshAccountStatus.isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            ) : connectAccount?.stripe_connect_account_id ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-yellow-400">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {connectAccount.stripe_connect_account_status === 'pending' 
                      ? 'Onboarding in progress'
                      : connectAccount.stripe_connect_account_status === 'restricted'
                      ? 'Account needs attention'
                      : 'Account setup incomplete'}
                  </span>
                </div>
                
                {/* Show helpful message based on requirements status */}
                {(() => {
                  const hasPendingVerification = (refreshStatusData?.pending_verification?.length || 0) > 0;
                  const hasOutstandingRequirements = (refreshStatusData?.currently_due?.length || 0) > 0 || 
                                                      (refreshStatusData?.past_due?.length || 0) > 0;
                  const needsAction = hasOutstandingRequirements && !hasPendingVerification;
                  
                  if (hasPendingVerification) {
                    // Stripe is processing - refresh will help
                    return (
                      <div className="space-y-2">
                        <p className="text-[#A9A3A3] text-sm">
                          Stripe is currently processing your verification information. This can take several minutes.
                        </p>
                        <p className="text-[#A9A3A3] text-sm font-medium">
                          💡 Click &quot;Refresh&quot; to check if verification is complete. No additional action needed from you.
                        </p>
                      </div>
                    );
                  } else if (needsAction) {
                    // User needs to complete additional steps
                    return (
                      <div className="space-y-2">
                        <p className="text-[#A9A3A3] text-sm">
                          Your Stripe Connect account needs additional information to complete setup.
                        </p>
                        <p className="text-[#A9A3A3] text-sm font-medium">
                          ⚠️ Refresh will not fix this. Click &quot;Complete Onboarding&quot; to return to Stripe and provide the required information.
                        </p>
                        {refreshStatusData?.incompleteReasons && refreshStatusData.incompleteReasons.length > 0 && (
                          <div className="mt-2 p-3 bg-[#2D2727] border border-[#504A4A] rounded-md">
                            <p className="text-[#A9A3A3] text-xs font-medium mb-1">Required actions:</p>
                            <ul className="text-[#A9A3A3] text-xs list-disc list-inside space-y-1">
                              {refreshStatusData.incompleteReasons.map((reason, idx) => (
                                <li key={idx}>{reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // Default message for pending status
                    return (
                      <div className="space-y-2">
                        <p className="text-[#A9A3A3] text-sm">
                          Please complete your Stripe Connect onboarding to start accepting payments.
                        </p>
                        <p className="text-[#A9A3A3] text-sm">
                          ⏱️ Stripe verification can take several minutes. Click &quot;Refresh&quot; to check your status.
                        </p>
                      </div>
                    );
                  }
                })()}
                
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      if (!currentCompanyId) return;
                      try {
                        const result = await getOnboardingLink.getLink(currentCompanyId, false);
                        if (result.url) {
                          window.location.href = result.url;
                        }
                      } catch (_err) {
                        showNotification('error', 'Failed to get onboarding link');
                      }
                    }}
                    disabled={getOnboardingLink.isLoading}
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] flex-1"
                    data-testid="billing-settings-complete-onboarding-button"
                  >
                    {getOnboardingLink.isLoading ? 'Loading...' : 'Complete Onboarding'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!currentCompanyId) return;
                      try {
                        const result = await refreshAccountStatus.refresh(currentCompanyId);
                        // Store refresh response data for UI messaging
                        setRefreshStatusData({
                          hasOutstandingRequirements: result.hasOutstandingRequirements,
                          currently_due: result.currently_due,
                          past_due: result.past_due,
                          pending_verification: result.pending_verification,
                          incompleteReasons: result.incompleteReasons,
                        });
                        refetchConnectAccount();
                        if (result.isOnboardingComplete) {
                          showNotification('success', 'Account onboarding completed!');
                          setRefreshStatusData(null); // Clear status data when complete
                        } else {
                          showNotification('success', 'Account status refreshed');
                        }
                      } catch (_err) {
                        showNotification('error', 'Failed to refresh account status');
                      }
                    }}
                    disabled={refreshAccountStatus.isLoading}
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A] flex items-center gap-2"
                    data-testid="billing-settings-refresh-status-button"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshAccountStatus.isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-[#A9A3A3]">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Payment account not set up</span>
                </div>
                <p className="text-[#A9A3A3] text-sm">
                  Connect your Stripe account to start accepting payments. Payments will go directly to your account, 
                  and you&apos;ll handle refunds and disputes through your Stripe Dashboard.
                </p>
                <Button
                  onClick={async () => {
                    if (!currentCompanyId) return;
                    try {
                      await createConnectAccount.create(currentCompanyId);
                      const result = await getOnboardingLink.getLink(currentCompanyId, false);
                      if (result.url) {
                        window.location.href = result.url;
                      }
                    } catch (_err) {
                      showNotification('error', 'Failed to create payment account');
                    }
                  }}
                  disabled={createConnectAccount.isLoading || getOnboardingLink.isLoading}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
                  data-testid="billing-settings-connect-stripe-account-button"
                >
                  {createConnectAccount.isLoading || getOnboardingLink.isLoading ? 'Setting up...' : 'Connect Stripe Account'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* RollOps Subscription - Only show for owners */}
        {isOwner && (
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">RollOps Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingSaaS ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
                <span className="ml-3 text-[#A9A3A3]">Loading subscription...</span>
              </div>
            ) : saasSubscription ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#EBFFEE]">Status:</span>
                  <div className="flex items-center gap-2">
                    {saasSubscription.status === 'active' ? (
                      <>
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 font-medium">Active</span>
                      </>
                    ) : saasSubscription.status === 'past_due' || saasSubscription.status === 'unpaid' ? (
                      <>
                        <AlertCircle className="w-5 h-5 text-red-400" />
                        <span className="text-red-400 font-medium">
                          {saasSubscription.status === 'past_due' ? 'Past Due' : 'Unpaid'}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-5 h-5 text-yellow-400" />
                        <span className="text-yellow-400 font-medium">
                          {saasSubscription.status === 'trialing' ? 'Trial' : saasSubscription.status.charAt(0).toUpperCase() + saasSubscription.status.slice(1)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#EBFFEE]">Plan:</span>
                  <span className="text-[#A9A3A3]">
                    {saasSubscription.plan_type.charAt(0).toUpperCase() + saasSubscription.plan_type.slice(1)} - {saasSubscription.billing_cycle === 'monthly' ? 'Monthly' : 'Annual'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#EBFFEE]">Price:</span>
                  <span className="text-[#A9A3A3]">
                    ${saasSubscription.price.toFixed(2)}/{saasSubscription.billing_cycle === 'monthly' ? 'month' : 'year'}
                  </span>
                </div>
                {(saasSubscription.status === 'past_due' || saasSubscription.status === 'unpaid' || saasSubscription.status === 'incomplete') && (
                  <div className="mt-4 p-3 bg-red-600/30 border border-red-600 rounded-md">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-white text-sm">
                        {saasSubscription.status === 'past_due' || saasSubscription.status === 'unpaid'
                          ? 'Your subscription payment failed. Please update your payment method to restore access.'
                          : 'Please add a payment method to complete your subscription setup.'}
                      </p>
                    </div>
                  </div>
                )}
                {saasSubscription.cancel_at_period_end && saasSubscription.cancellation_effective_date && (
                  <div className="mt-4 p-3 bg-yellow-600/20 border border-yellow-600/50 rounded-md">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 text-sm font-medium">
                          Cancellation Scheduled
                        </p>
                        <p className="text-white text-sm mt-1">
                          Your subscription will be cancelled on {new Date(saasSubscription.cancellation_effective_date).toLocaleDateString()}.
                          You will continue to have access until then.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {saasSubscription.status === 'active' && !saasSubscription.cancel_at_period_end && (
                  <div className="mt-4 pt-4 border-t border-[#504A4A]">
                    <button
                      onClick={async () => {
                        if (!confirm(
                          saasSubscription.billing_cycle === 'annual' && saasSubscription.billing_end_date
                            ? `Your subscription will remain active until ${new Date(saasSubscription.billing_end_date).toLocaleDateString()} (end of your paid period). You will not be charged again. Continue?`
                            : 'Your subscription will be cancelled at the end of the current billing period. You will continue to have access until then. Continue?'
                        )) {
                          return;
                        }
                        
                        try {
                          const { data, error } = await supabase.functions.invoke('cancel-saas-subscription', {});
                          if (error) {
                            alert(`Failed to cancel subscription: ${error.message}`);
                          } else {
                            alert(data.message || 'Subscription cancellation scheduled successfully');
                            window.location.reload();
                          }
                        } catch (err) {
                          alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
                        }
                      }}
                      className="px-4 py-2 text-sm bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] rounded-lg transition-colors flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel Subscription
                    </button>
                  </div>
                )}
                <div className="mt-4 text-sm text-[#A9A3A3]">
                  <p>
                    Your RollOps subscription will be set up automatically. If you need to add a payment method, please contact support.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-[#A9A3A3] text-sm">No subscription found</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Billing Strategy - Only show if RollOps provider */}
        {formData.payment_provider === 'rollops' && !isOnTrial && (
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Subscription Billing Strategy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">When to bill subscribers</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="billing_strategy"
                    value="monthly_fixed_date"
                    checked={formData.billing_strategy === 'monthly_fixed_date'}
                    onChange={() => setFormData({ ...formData, billing_strategy: 'monthly_fixed_date', fixed_billing_day: formData.fixed_billing_day || 1 })}
                    className="w-4 h-4 text-rollops-accent"
                    data-testid="billing-settings-billing-strategy-monthly-fixed-date-radio"
                  />
                  <span className="text-[#EBFFEE]">Fixed monthly date</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="billing_strategy"
                    value="on_signup"
                    checked={formData.billing_strategy === 'on_signup'}
                    onChange={() => setFormData({ ...formData, billing_strategy: 'on_signup', fixed_billing_day: null })}
                    className="w-4 h-4 text-rollops-accent"
                    data-testid="billing-settings-billing-strategy-on-signup-radio"
                  />
                  <span className="text-[#EBFFEE]">On signup date (same day each month based on enrollment)</span>
                </label>
              </div>
            </div>

            {formData.billing_strategy === 'monthly_fixed_date' && (
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Billing day (1-31)</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.fixed_billing_day || ''}
                  onChange={(e) => setFormData({ ...formData, fixed_billing_day: parseInt(e.target.value) || null })}
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                  data-testid="billing-settings-fixed-billing-day-input"
                />
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Fee Schedule (read-only for gym owners) */}
        {formData.payment_provider === 'rollops' && !isOnTrial && (
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Fee Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingFeeSchedule ? (
              <p className="text-[#A9A3A3] text-sm">Loading fee schedule...</p>
            ) : feeSchedule ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                    Current Schedule
                  </label>
                  <p className="text-[#EBFFEE] text-lg font-semibold">
                    {feeSchedule.schedule_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Fee Breakdown
                  </label>
                  <div className="space-y-2">
                    {feeSchedule.items.map((item) => {
                      const recipientInfo = FEE_RECIPIENT_LABELS[item.fee_recipient] || { label: item.fee_recipient, description: '' };
                      const isPassedToCustomer = item.charge_effect === 'add';

                      return (
                        <div
                          key={item.fee_identifier}
                          className="px-3 py-2 bg-[#363232] rounded-md"
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-[#EBFFEE] text-sm">{item.line_item_heading}</span>
                            <span className="text-[#A9A3A3] text-sm font-mono">
                              {formatFee(item)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                              isPassedToCustomer
                                ? 'bg-amber-900/30 text-amber-300'
                                : 'bg-blue-900/30 text-blue-300'
                            }`}>
                              {isPassedToCustomer ? 'Customer pays' : 'You absorb'}
                            </span>
                            <span className="text-[#A9A3A3] text-xs">
                              → {recipientInfo.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p className="text-[#A9A3A3] text-xs">
                  Contact support to change your fee schedule.
                </p>
              </>
            ) : (
              <p className="text-[#A9A3A3] text-sm">No fee schedule assigned.</p>
            )}
          </CardContent>
        </Card>
        )}

        {/* Late Fee Configuration */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Late Fee Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Late fee strategy</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="late_fee_strategy"
                    value="staged"
                    checked={formData.late_fee_strategy === 'staged'}
                    onChange={() => {
                      const currentConfig = formData.late_fee_config;
                      const newConfig = Array.isArray(currentConfig) && currentConfig.length > 0 
                        ? currentConfig 
                        : [{ days: 0, amount: 0 }];
                      setFormData({ ...formData, late_fee_strategy: 'staged', late_fee_config: newConfig });
                    }}
                    className="w-4 h-4 text-rollops-accent"
                    data-testid="billing-settings-late-fee-strategy-staged-radio"
                  />
                  <span className="text-[#EBFFEE]">Staged fees (multiple fees over time)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="late_fee_strategy"
                    value="max_total"
                    checked={formData.late_fee_strategy === 'max_total'}
                    onChange={() => {
                      const daysUntilCancellation = isMaxTotalConfig(formData.late_fee_config) 
                        ? formData.late_fee_config.days_until_cancellation 
                        : (formData.auto_cancellation_days_overdue || 30);
                      setFormData({ 
                        ...formData, 
                        late_fee_strategy: 'max_total', 
                        late_fee_config: { max_amount: 0, days_until_cancellation: daysUntilCancellation },
                        // Sync auto-cancellation if enabled
                        auto_cancellation_days_overdue: formData.auto_cancellation_enabled ? daysUntilCancellation : formData.auto_cancellation_days_overdue,
                      });
                    }}
                    className="w-4 h-4 text-rollops-accent"
                    data-testid="billing-settings-late-fee-strategy-max-total-radio"
                  />
                  <span className="text-[#EBFFEE]">Accumulating late fee (with max amount and period)</span>
                </label>
              </div>
            </div>

            {formData.late_fee_strategy === 'staged' && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-[#EBFFEE]">Late fee stages</label>
                  <button
                    onClick={addLateFeeStage}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-[#221F1F] border border-[#EBFFEE] border-dashed rounded-md text-[#EBFFEE] text-base font-semibold hover:bg-[#363232] transition-colors"
                    data-testid="billing-settings-add-late-fee-stage-button"
                  >
                    <Plus className="w-5 h-5" />
                    Add Stage
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2 px-1">
                  <div className="text-sm font-semibold text-[#A9A3A3]">Days Late</div>
                  <div className="text-sm font-semibold text-[#A9A3A3]">Fee Amount</div>
                </div>
                <div className="space-y-2">
                  {(formData.late_fee_config as LateFeeStage[]).map((stage, index) => (
                    <div key={index} className="flex gap-2 items-center bg-[#363232] p-3 rounded">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Days overdue"
                        value={stage.days || ''}
                        onChange={(e) => handleDaysChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#221F1F] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                        maxLength={4}
                        data-testid={`billing-settings-late-fee-stage-${index}-days-input`}
                      />
                      <span className="text-[#A9A3A3]">=</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="$0.00"
                        value={getCurrencyDisplayValue(index, stage.amount)}
                        onChange={(e) => handleCurrencyChange(index, e.target.value)}
                        onBlur={() => handleCurrencyBlur(index)}
                        className="flex-1 px-3 py-2 bg-[#221F1F] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                        maxLength={15}
                        data-testid={`billing-settings-late-fee-stage-${index}-amount-input`}
                      />
                      <Button
                        onClick={() => removeLateFeeStage(index)}
                        className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] p-2"
                        data-testid={`billing-settings-late-fee-stage-${index}-remove-button`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {Array.isArray(formData.late_fee_config) && formData.late_fee_config.length === 0 && (
                    <p className="text-[#A9A3A3] text-sm">No late fee stages configured. Click &quot;Add Stage&quot; to add one.</p>
                  )}
                </div>
              </div>
            )}

            {formData.late_fee_strategy === 'max_total' && (
              <div className="space-y-4">
                <p className="text-[#A9A3A3] text-sm mb-4">
                  Late fees accumulate daily up to a maximum amount over a maximum period. Once the maximum is reached or the period ends, late fees stop accumulating.
                </p>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Maximum late fee amount ($)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="$0.00"
                    value={maxAmountInput !== '' ? maxAmountInput : (isMaxTotalConfig(formData.late_fee_config) && formData.late_fee_config.max_amount > 0 ? formatCurrencyDisplay(formData.late_fee_config.max_amount) : '')}
                    onChange={(e) => {
                      if (e.target.value.length > 15) return;
                      setMaxAmountInput(e.target.value);
                      const numericValue = parseCurrencyInput(e.target.value);
                      setFormData({
                        ...formData,
                        late_fee_config: {
                          ...(isMaxTotalConfig(formData.late_fee_config) ? formData.late_fee_config : { max_amount: 0, days_until_cancellation: 30 }),
                          max_amount: numericValue,
                        },
                      });
                    }}
                    onBlur={() => {
                      const numericValue = parseCurrencyInput(maxAmountInput);
                      setMaxAmountInput('');
                      setFormData({
                        ...formData,
                        late_fee_config: {
                          ...(isMaxTotalConfig(formData.late_fee_config) ? formData.late_fee_config : { max_amount: 0, days_until_cancellation: 30 }),
                          max_amount: numericValue,
                        },
                      });
                    }}
                    className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    maxLength={15}
                    data-testid="billing-settings-max-late-fee-amount-input"
                  />
                  <p className="text-[#A9A3A3] text-sm mt-1">
                    Maximum total late fees that can accumulate on an overdue payment
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Maximum late period (days)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Days"
                    value={isMaxTotalConfig(formData.late_fee_config) ? formData.late_fee_config.days_until_cancellation || '' : ''}
                    onChange={(e) => {
                      if (e.target.value.length > 4) return;
                      const numericValue = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                      const daysUntilCancellation = numericValue > 0 ? numericValue : 30;
                      setFormData({
                        ...formData,
                        late_fee_config: {
                          ...(isMaxTotalConfig(formData.late_fee_config) ? formData.late_fee_config : { max_amount: 0, days_until_cancellation: 30 }),
                          days_until_cancellation: daysUntilCancellation,
                        },
                        // Sync auto-cancellation if enabled
                        auto_cancellation_days_overdue: formData.auto_cancellation_enabled ? daysUntilCancellation : formData.auto_cancellation_days_overdue,
                      });
                    }}
                    className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    maxLength={4}
                    data-testid="billing-settings-max-late-fee-days-until-cancellation-input"
                  />
                  <p className="text-[#A9A3A3] text-sm mt-1">
                    Maximum number of days late fees can accumulate. After this period, late fees stop accumulating.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cancellation Notice */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Cancellation Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Cancellation notice period (days)</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Days"
                value={formData.cancellation_notice_days || ''}
                onChange={(e) => {
                  if (e.target.value.length > 4) return;
                  const numericValue = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                  setFormData({ ...formData, cancellation_notice_days: numericValue });
                }}
                className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                maxLength={4}
                data-testid="billing-settings-cancellation-notice-days-input"
              />
              <p className="text-[#A9A3A3] text-sm mt-1">
                Number of days notice required before subscription cancellation takes effect
              </p>
            </div>

            {/* Auto-Cancellation Settings */}
            <div className="pt-4 border-t border-[#504A4A]">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE]">Automatic Cancellation</label>
                  <p className="text-[#A9A3A3] text-sm mt-1">
                    When enabled, subscriptions are automatically cancelled after the maximum late period. When disabled, gym owners must manually cancel subscriptions (class registration access is already blocked for overdue accounts).
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={formData.auto_cancellation_enabled || false}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      // If using max_total strategy and enabling, sync with days_until_cancellation
                      let daysOverdue = formData.auto_cancellation_days_overdue;
                      if (enabled && formData.late_fee_strategy === 'max_total' && isMaxTotalConfig(formData.late_fee_config)) {
                        daysOverdue = formData.late_fee_config.days_until_cancellation || 30;
                      } else if (enabled && !daysOverdue) {
                        daysOverdue = 30;
                      }
                      setFormData({
                        ...formData,
                        auto_cancellation_enabled: enabled,
                        auto_cancellation_days_overdue: enabled ? daysOverdue : null,
                      });
                    }}
                    className="w-4 h-4 text-rollops-accent rounded focus:ring-[#9B1421]"
                    data-testid="billing-settings-auto-cancellation-enabled-checkbox"
                  />
                  <span className="text-sm text-[#EBFFEE] whitespace-nowrap">Enable auto-cancellation</span>
                </label>
              </div>
              {formData.auto_cancellation_enabled && (
                <div className="mt-3">
                  {formData.late_fee_strategy === 'max_total' ? (
                    <div className="bg-[#363232] border border-[#504A4A] rounded-md p-3">
                      <p className="text-[#EBFFEE] text-sm font-medium mb-1">Auto-cancellation date</p>
                      <p className="text-[#A9A3A3] text-sm">
                        Automatically set to {isMaxTotalConfig(formData.late_fee_config) ? formData.late_fee_config.days_until_cancellation || 30 : 30} days (matches maximum late period). Subscriptions will be cancelled after this many days of overdue payment.
                      </p>
                    </div>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Days overdue before auto-cancellation</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Days"
                        value={formData.auto_cancellation_days_overdue || ''}
                        onChange={(e) => {
                          if (e.target.value.length > 4) return;
                          const numericValue = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                          setFormData({ ...formData, auto_cancellation_days_overdue: numericValue > 0 ? numericValue : null });
                        }}
                        className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                        maxLength={4}
                        data-testid="billing-settings-auto-cancellation-days-overdue-input"
                      />
                      <p className="text-[#A9A3A3] text-sm mt-1">
                        Subscriptions will be automatically cancelled after this many days of overdue payment
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Additional Terms and Conditions */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Additional Terms and Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                Add any additional terms and conditions for your subscription contracts
              </label>
              <p className="text-[#A9A3A3] text-sm mb-3">
                These terms will be displayed to students when they enroll in subscriptions. Use the toolbar below to format your text.
              </p>
              <RichTextEditor
                content={formData.additional_terms_and_conditions || ''}
                onChange={(html) => setFormData({ ...formData, additional_terms_and_conditions: html })}
                placeholder="Enter additional terms and conditions..."
              />
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Save Button - Floating */}
        <Button
          onClick={handleSave}
          disabled={updateMutation.isLoading}
          className="fixed bottom-6 right-6 z-50 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-6 py-3 flex items-center gap-2 shadow-lg rounded-lg"
          data-testid="billing-settings-save-button"
        >
          <Save className="w-4 h-4" />
          {updateMutation.isLoading ? 'Saving...' : 'Save Settings'}
        </Button>
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      <ConfirmDialog
        isOpen={showUnsavedModal}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        issues={validateBillingSettings()}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
        confirmText="Leave Without Saving"
        cancelText="Stay and Save"
      />
    </>
  );
}

